import { AxiosResponse } from 'axios';
import isString from 'lodash/isString';

import { FEATURE_FLAG, ISSUE_TYPE } from '@qality/common-config';
import {
  DEFAULT_JIRA_FIELDS,
  GetAllIssuesByJQL,
  Issue,
  IssueSearchResponse,
  JiraField,
} from '@qality/common-model';
import { APResponse } from '@qality/common-model/src/AP/AP';
import { Utils } from '@qality/common-utils';

import StorageOfUniqueServices from './StorageOfUniqueServices';
import { AxiosWrapperService as axios } from './index';

export type IssueType = {
  name: string;
};

export default {
  async getIssue(
    issueIdOrKey: string,
    getRenderedFields = false
  ): Promise<Issue> {
    const additionalFields = getRenderedFields
      ? '?expand=renderedFields&fields=*all'
      : '';
    const response = await StorageOfUniqueServices.get('API').request({
      url: `/rest/api/2/issue/${issueIdOrKey}${additionalFields}`,
      type: 'GET',
    });
    return JSON.parse(response.body);
  },

  createIssue(
    summary: string,
    issuetypeId: number,
    projectId: number,
    retry = true
  ): Promise<AxiosResponse | APResponse> {
    return StorageOfUniqueServices.get('API')[retry ? 'request' : 'noRetry']({
      url: '/rest/api/2/issue',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        fields: {
          summary: summary,
          issuetype: {
            id: issuetypeId,
          },
          project: {
            id: projectId,
          },
        },
      }),
    });
  },

  async getCreateIssueMetadata(projectKey: string): Promise<Array<IssueType>> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_PROJECT_ISSUE_TYPES +
        '/' +
        projectKey
    );
    const response = await axios.get(url);

    if (isString(response.data)) {
      response.data = JSON.parse(response.data);
    }

    return response.data;
  },

  updateIssue(issueIdOrKey: string, summary: string): Promise<Issue> {
    return StorageOfUniqueServices.get('API').request({
      url: `/rest/api/2/issue/${issueIdOrKey}`,
      type: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify({
        update: {
          summary: [
            {
              set: summary,
            },
          ],
        },
      }),
    }) as unknown as Promise<Issue>;
  },

  issueLink(
    outwardIssueKey: string,
    inwardIssue: string,
    issueLinkTypeId: string | number
  ): Promise<{
    body: string;
    xhr?: {
      status: number;
    };
  }> {
    return StorageOfUniqueServices.get('API').request({
      url: '/rest/api/2/issueLink',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        outwardIssue: {
          key: outwardIssueKey,
        },
        inwardIssue: {
          key: inwardIssue,
        },
        type: {
          id: issueLinkTypeId,
        },
      }),
    });
  },

  async getFilter(filterId: string): Promise<{ jql: string }> {
    return StorageOfUniqueServices.get('API')
      .request({
        url: `/rest/api/2/filter/${filterId}`,
        type: 'GET',
        contentType: 'application/json',
      })
      .then((response) => JSON.parse(response.body))
      .catch((response) => JSON.parse(response.err));
  },

  async pickerApi(query: string, jql: string): Promise<Array<Issue>> {
    const url =
      '/rest/api/2/issue/picker?showSubTasks=true&showSubTaskParent=true';
    const params = `&query=${query}&currentJQL=${jql}&maxResults=20`;

    return StorageOfUniqueServices.get('API')
      .request(url + params)
      .then((response) => {
        const data = JSON.parse(response.body);

        const [historySearch, currentSearch] = data.sections;
        const pickerIssues = {
          issues: [
            ...(historySearch?.issues ?? []),
            ...(currentSearch?.issues ?? []),
          ],
        };

        pickerIssues.issues = pickerIssues.issues.map((pickerIssue) =>
          this.mapPickerIssueToSearchIssueFormat(pickerIssue)
        );

        return pickerIssues;
      });
  },

  useSearchIssuePicker(text: string): boolean {
    return FEATURE_FLAG.useSearchIssuePicker && Utils.isNilOrEmpty(text);
  },

  mapPickerIssueToSearchIssueFormat(pickerIssue) {
    const { id, key, img, summaryText } = pickerIssue;

    const iconUrl = StorageOfUniqueServices.get('URLService').getAvatarUrl(img);

    return {
      id,
      key,
      fields: {
        summary: summaryText,
        issuetype: {
          iconUrl,
        },
      },
    };
  },

  async searchIssuesByQuery(
    query = '',
    projectKey: string = null,
    excludedIds: Array<string | number> = [],
    issuetype: string = null,
    usePicker = false // very likely for a user to not have enough test cases in history to populate the select
  ): Promise<IssueSearchResponse> {
    const text = query.trim();

    const shouldUsePicker = this.useSearchIssuePicker(text) && usePicker;

    const jql = this.buildJQLFromParams(
      query,
      projectKey,
      excludedIds,
      issuetype,
      shouldUsePicker
    );

    const fields: Array<JiraField> = [
      'summary',
      'issuetype',
      'created',
      'status',
    ];

    try {
      return shouldUsePicker
        ? await this.pickerApi(text, jql)
        : await StorageOfUniqueServices.get(
            'JQLSearchService'
          ).getIssuePageByJQL({
            jql,
            fields,
            maxResults: 20,
          });
    } catch (e) {
      return { issues: [] };
    }
  },

  buildJQLFromParams(
    query: string,
    projectKey: string,
    excludedIds: Array<string | number>,
    issuetype: string,
    shouldUsePicker: boolean
  ): string {
    const text = query.trim();
    let jql = '';
    const IdsNotIn = excludedIds.filter(Boolean);

    if (projectKey) {
      jql += `project='${projectKey}' AND `;
    }

    // do NOT include text or key to JQL when we are going to use /picker
    if (!shouldUsePicker) {
      if (/(^([a-zA-Z]+)-(\d+)$)/.test(text)) {
        jql += `key = '${text}' AND `;
      } else if (text.length > 0) {
        jql += `summary ~ "${text}" AND `;
      }
    }

    if (IdsNotIn.length > 0) {
      jql += `id NOT IN (${IdsNotIn.join(',')}) AND `;
    }

    if (issuetype) {
      jql += `issuetype = '${issuetype}' `;
    } else {
      jql += "issuetype != 'QAlity Test' ";
    }

    jql += 'ORDER BY created DESC';
    return jql;
  },

  getIssuesFromList(
    ids: Array<number>,
    issues: { [key: number]: Issue }
  ): {
    foundIssues: Array<Issue>;
    notFoundCount: number;
  } {
    const foundIssues = ids.map((issueId) => issues[issueId]).filter(Boolean);
    const notFoundCount = ids.length - foundIssues.length;

    return {
      foundIssues,
      notFoundCount,
    };
  },

  getQAlityTypeIssues(issues: Array<Issue>): Array<Issue> {
    return issues.filter((i) => {
      return i.fields.issuetype.name === ISSUE_TYPE.QALITY_TEST;
    });
  },

  async getAllIssuesByJqlFilteredByQAlityType({
    jql,
    fields = DEFAULT_JIRA_FIELDS,
    expand = [],
  }: GetAllIssuesByJQL): Promise<Array<Issue>> {
    const issues = await StorageOfUniqueServices.get(
      'JQLSearchService'
    ).getAllIssuesByJql({
      jql,
      fields,
      expand,
    });
    return this.getQAlityTypeIssues(issues);
  },
};
